import * as React from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/EditOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";

import { useContextsStore } from "../../stores/useContextsStore";
import { useSnackbar } from "../Snackbar";
import { replaceSpaceWithUnderscore, isStringSafe } from "../../utils/index";
import {
	FavoriteIconButton,
	ShareIconButton,
	BrainIconButton,
	HEADER_ICON_SX,
} from "./HeaderActions";

const selectorShowSnackbar = (state) => state.showSnackbar;
const selectorRenameContext = (state) => state.renameContext;

/**
 * Layout (always-visible, all three icons same size):
 *   [★ favorite] [title text] [✎ edit] [↗ share]
 *
 * Favorite is filled-only; color reflects state (orange when favorited,
 * text.disabled when not). Edit + share are outlined; share is hidden
 * for embed mode, edit is hidden for read-only public viewers.
 */
export default function EditableTitle({ editable, label, onTitleChange }) {
	const [title, setTitle] = React.useState(label);
	const [originalTitle, setOriginalTitle] = React.useState(label);
	const [isTitleFocused, setIsTitledFocused] = React.useState(false);
	const showSnackbar = useSnackbar(selectorShowSnackbar);
	const renameContext = useContextsStore(selectorRenameContext);

	const theme = useTheme();

	React.useEffect(() => {
		setTitle(label);
		setOriginalTitle(label);
	}, [label]);

	// Public viewer of someone else's graph: render title only, no actions.
	if (!editable) {
		return (
			<Box
				sx={{
					flexGrow: 1,
					ml: 2,
					display: "flex",
					alignItems: "center",
					minWidth: 0,
					gap: 0.25,
				}}
			>
				<Typography
					variant="h6"
					color="inherit"
					noWrap
					sx={{
						color: (theme) => theme.palette.text.primary,
						// Grow with the screen so the full title (e.g. "Welcome to
						// KeywordGraph, <username>") shows on wide viewports, and
						// only ellipsis-truncate when it genuinely can't fit.
						maxWidth: { xs: "240px", sm: "50vw" },
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
					}}
				>
					{title}
				</Typography>
				<ShareIconButton contextName={label} />
				<BrainIconButton contextName={label} />
			</Box>
		);
	}

	const saveTitle = async () => {
		if (!title) {
			showSnackbar("error", `Please, provide the name for the graph context.`);
			setTitle(label);
			return;
		}
		let newTitle = replaceSpaceWithUnderscore(title);
		const newNamePassesTest = isStringSafe(newTitle);
		if (!newNamePassesTest) {
			showSnackbar(
				"error",
				`Please, only use latin letters, numbers, underscores, or dashes for the context name.`,
			);
			setTitle(label);
			return;
		}
		const newContextName = newTitle;
		const renamedContext = await renameContext(newContextName, label);
		showSnackbar(
			"success",
			`The context has been successfully renamed to ${newContextName}`,
		);
		const labelToReplace = renamedContext ? renamedContext : newContextName;
		onTitleChange(label, labelToReplace);
		window.location.href = window.location.href.replace(
			`/${label}/`,
			`/${labelToReplace}/`,
		);
	};

	// Fixed-width slot for the title (or its inline edit field). The icons
	// on the right used to shift sideways every time the user clicked to
	// edit because TextField's intrinsic width differed from Typography's.
	// Pinning a width keeps the row stable; long titles ellipsis-truncate
	// in display mode and scroll inside the input in edit mode.
	const titleSlotSx = {
		width: 140,
		flexShrink: 0,
		[theme.breakpoints.up("sm")]: {
			width: 170,
		},
	};

	return (
		<Box
			sx={{
				flexGrow: 1,
				ml: 2,
				height: "100%",
				display: "flex",
				alignItems: "center",
				gap: 0.25,
			}}
		>
			{isTitleFocused ? (
				<>
					<Box sx={titleSlotSx}>
						<TextField
							autoFocus
							size="small"
							variant="standard"
							fullWidth
							value={title}
							onChange={(event) => setTitle(event.target.value)}
							onMouseUp={() => {
								setIsTitledFocused(false);
								setTitle(originalTitle);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									setIsTitledFocused(false);
									saveTitle();
								}
								if (e.key === "Escape") {
									setIsTitledFocused(false);
									setTitle(originalTitle);
								}
							}}
							sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
							inputProps={{ sx: { fontSize: "1.25rem" } }}
						/>
					</Box>
					<Tooltip title="Save name">
						<IconButton
							aria-label="save-graph-name"
							size="small"
							onClick={() => {
								setIsTitledFocused(false);
								saveTitle();
							}}
							sx={{
								...HEADER_ICON_SX,
								color: (theme) => theme.palette.text.primary,
							}}
						>
							<SaveIcon />
						</IconButton>
					</Tooltip>
				</>
			) : (
				<>
					<Box sx={titleSlotSx}>
						<Typography
							variant="h6"
							color="inherit"
							noWrap
							sx={{
								cursor: "pointer",
								color: (theme) => theme.palette.text.primary,
								overflow: "hidden",
								whiteSpace: "nowrap",
								textOverflow: "ellipsis",
							}}
							onClick={() => setIsTitledFocused(true)}
						>
							{title}
						</Typography>
					</Box>
					{/* Edit — always visible (was hover-gated), unified size */}
					<Tooltip title="Rename graph">
						<IconButton
							aria-label="edit-graph-name"
							size="small"
							onClick={() => setIsTitledFocused(true)}
							sx={{
								...HEADER_ICON_SX,
								color: (theme) => theme.palette.text.secondary,
								"&:hover": {
									color: (theme) => theme.palette.text.primary,
								},
							}}
						>
							<EditIcon />
						</IconButton>
					</Tooltip>
				</>
			)}

			{/* Right-side action group: favorite + share + brain, unified size with edit. */}
			<FavoriteIconButton contextName={label} />
			<ShareIconButton contextName={label} />
			<BrainIconButton contextName={label} />
		</Box>
	);
}
